import React from 'react';
import {
  Alert, Box, CircularProgress, Stack, TextField, Typography,
} from '@mui/material';
import { ChargebeePlanOption } from '../features/plans/planPricing';

export interface PlanChargebeeFieldProps {
  // Chargebee has not answered yet.
  loading: boolean;
  loadError: string | null;
  // The Chargebee plan with the plan's id, when Chargebee has one.
  attached: ChargebeePlanOption | undefined;
  planId: string;
  rrp: string;
  cost: string;
  onRrp: (rrp: string) => void;
}

/**
 * What the customer is billed and what they are shown. The Chargebee plan is
 * the one with the plan's own id: there is nothing to choose. When Chargebee
 * has it, the RRP is its price and cannot be typed; when it does not, the
 * RRP is entered once and the plan is created in Chargebee on save. The
 * displayed price is never typed: it is the RRP less the plan coupon.
 */
const PlanChargebeeField = ({
  loading, loadError, attached, planId, rrp, cost, onRrp,
}: PlanChargebeeFieldProps) => {
  const id = planId.trim();
  const creating = !loading && !loadError && !attached;

  let status = (
    <Alert severity="info" icon={<CircularProgress size={18} />}>
      Reading Chargebee…
    </Alert>
  );
  if (loadError) {
    status = (
      <Alert severity="warning">
        {`Could not read Chargebee: ${loadError}. The prices below are as stored.`}
      </Alert>
    );
  } else if (attached) {
    status = (
      <Alert severity="success">
        {`Chargebee already has plan "${attached.id}" (${attached.name}) at $${attached.price} per month. `
          + 'It is used as it is and is not changed from here.'}
      </Alert>
    );
  } else if (creating) {
    status = (
      <Alert severity="info">
        {id === ''
          ? 'Enter the plan id: if Chargebee has a plan with that id its price is used, otherwise one is created.'
          : `Chargebee has no plan "${id}". It will be created in Chargebee when this plan is saved: `
            + 'billed monthly, at the RRP below.'}
      </Alert>
    );
  }

  let rrpHelp = 'The price of the Chargebee plan. It is changed in Chargebee, not here.';
  if (creating) rrpHelp = 'The price the plan is created with in Chargebee. It cannot be changed here afterwards.';

  return (
    <Box>
      <Typography variant="subtitle2">Billing and price</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        The customer is charged the RRP, less the plan coupon while it lasts.
        The signup site shows the price after the coupon.
      </Typography>
      <Stack spacing={2}>
        {status}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="RRP ($ per month)"
            value={rrp}
            onChange={(e) => onRrp(e.target.value)}
            disabled={!creating}
            helperText={rrpHelp}
            fullWidth
          />
          <TextField
            label="Price shown on the signup site ($ per month)"
            value={cost}
            disabled
            helperText="Worked out, never typed: the RRP less the plan coupon, or the RRP when no coupon applies."
            fullWidth
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default PlanChargebeeField;
